import React, { useEffect, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Box,
  IconButton,
  Collapse,
  TextField,
  Grid,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import { ArrowDropDown, ArrowDropUp } from "@material-ui/icons";
import { StockAdjustFormData } from "./AdjustStockLevelsForm";
import { StockTypeUpdateFormData } from "../stockTypeUpdate/NewStockTypeUpdateForm";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  reasons,
  convertToMaxQty,
  getNqQuantity,
} from "./ManualStockAdjustmentUtils";
import { stockTypeTransferMapping } from "../stockTypeUpdate/StockTypeUpdateUtils";
import "./BatchComponent.css";

const customMuiInputStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#EAEAEA",
    },
    "&:hover fieldset": {
      borderColor: "#B9B9B9",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1C7EA0",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#B5B5B5",
    fontSize: "13px",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#1C7EA0",
  },
  "& .MuiInputAdornment-root": {
    color: "#909090",
  },
  "& .Mui-disabled": {
    borderColor: "#EAEAEA",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#EAEAEA",
    },
  },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    {
      "-webkit-appearance": "none",
      margin: 0,
    },
};

const selectedLightBlueTheme = {
  backgroundColor: "#F5FCFF",
  border: "1px solid #4BADE2",
  "&:hover": {
    borderColor: "#4BADE2",
  },
};

const nonEditableGreyTheme = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FAFAFA",
    "& fieldset": {
      border: "none",
    },
  },
  "& .MuiInputAdornment-root": {
    color: "#909090",
  },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    {
      "-webkit-appearance": "none",
      margin: 0,
    },
};

interface BatchComponentV2Props {
  formData: StockAdjustFormData | StockTypeUpdateFormData;
  updateBatchWiseData: (fieldName: string, value: string) => void;
  updateBatch: () => void;
  clearBatch: () => void;
  tempBatchData: {
    [key: string]: any;
  };
  currentPc: string;
  currentCs: string;
  currentOther: string;
  mandatoryFieldError: boolean;
  quantityError: boolean;
  uom: string;
  otherUnitName?: string
}

const BatchComponentV2: React.FC<BatchComponentV2Props> = ({
  formData,
  updateBatchWiseData,
  currentCs,
  currentOther,
  currentPc,
  mandatoryFieldError,
  quantityError,
  clearBatch,
  updateBatch,
  tempBatchData,
  uom,
  otherUnitName
}) => {
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
  const { caseUnitLabel, otherUnitLabel, packUnitLabel } = ConfigDictionary;
  const disableCs = !uom?.toLowerCase().includes("cs") && !uom?.toLowerCase().includes("all");
  const disablePc = !uom?.toLowerCase().includes("ea") && !uom?.toLowerCase().includes("all");
  const disableOther = !uom?.toLowerCase().includes("other") && !uom?.toLowerCase().includes("all");

  const enableDropDowns =
    tempBatchData?.quantityCs || tempBatchData?.quantityPc || tempBatchData?.quantityOther;
  const [stockUpdateReasons, setStockUpdateReasons] = useState<string[]>([]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (["e", "E", "+", "-"].includes(event.key)) {
      event.preventDefault(); // Disallow "e", "E", "+", and "-" keys
    }
  };

  const updateReasons = () => {
    const negativeReasons = [
      "Damaged",
      "Expired",
      "Quality Inspection Failed",
      "Inventory Reconciliation",
    ];
    const positiveReasons = ["Stock Good for Selling"];

    if (
      formData.stockType?.toLowerCase() === "returned" &&
      tempBatchData?.updatedType?.toLowerCase() === "available"
    ) {
      setStockUpdateReasons(positiveReasons);
    } else {
      setStockUpdateReasons(negativeReasons);
    }
  };

  // Handle focus out to normalize quantities (following senior's pattern)
  const handleFocusOut = async () => {
    const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
    
    if (!ConfigDictionary.uomConversion) {
      return;
    }

    try {
      const qtyObject = {
        skuCode: formData.skuCode,
        totalCaseQty: tempBatchData?.quantityCs ? parseInt(tempBatchData.quantityCs) : 0,
        totalPieceQty: tempBatchData?.quantityPc ? parseInt(tempBatchData.quantityPc) : 0,
        totalOtherQty: tempBatchData?.quantityOther ? parseInt(tempBatchData.quantityOther) : 0,
      };

      await convertToMaxQty(qtyObject, formData.skuCode, uom);
      
      const normalizedBatchData = {
        ...tempBatchData,
        quantityCs: qtyObject.totalCaseQty + "",
        quantityPc: qtyObject.totalPieceQty + "",
        quantityOther: qtyObject.totalOtherQty + ""
      };
      
      // Update the state through the parent's updateBatchWiseData function
      Object.keys(normalizedBatchData).forEach(key => {
        if (key === 'quantityCs' || key === 'quantityPc' || key === 'quantityOther') {
          updateBatchWiseData(key, normalizedBatchData[key]);
        }
      });
    } catch (error) {
      console.error("Error in handleFocusOut:", error);
    }
  };

  useEffect(() => {
    updateReasons();
  }, [formData.stockType, tempBatchData?.updatedType]);

  // Auto-prefill updatedType when there's only one option available
  useEffect(() => {
    const availableOptions = stockTypeTransferMapping[formData.stockType];
    if (availableOptions && availableOptions.length === 1 && !tempBatchData?.updatedType) {
      updateBatchWiseData("updatedType", availableOptions[0]);
    }
  }, [formData.stockType, tempBatchData?.updatedType]);

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={3}>
        <span className="batch_text">Current Quantity</span>
      </Grid>
      <Grid item xs={otherUnitLabel ? 3 : 4.5}>
        <TextField
          fullWidth
          name="quantityCs"
          type="number"
          value={currentCs}
          variant="outlined"
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <span>{caseUnitLabel}</span>{" "}
              </InputAdornment>
            ),
          }}
          disabled={true}
          sx={nonEditableGreyTheme}
          inputProps={{ readOnly: true }}
        />
      </Grid>
      {otherUnitLabel && (
        <Grid item xs={otherUnitLabel ? 3 : 4.5}>
          <TextField
            fullWidth
            name="quantityOther"
            type="number"
            value={currentOther}
            variant="outlined"
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <span>{otherUnitName? otherUnitName:otherUnitLabel}</span>
                </InputAdornment>
              ),
            }}
            disabled={true}
            sx={nonEditableGreyTheme}
            inputProps={{ readOnly: true }}
          />
        </Grid>
      )}
      <Grid item xs={otherUnitLabel ? 3 : 4.5}>
        <TextField
          fullWidth
          name="quantityPc"
          type="number"
          value={currentPc}
          variant="outlined"
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <span>{packUnitLabel}</span>
              </InputAdornment>
            ),
          }}
          disabled={true}
          sx={nonEditableGreyTheme}
          inputProps={{ readOnly: true }}
        />
      </Grid>
      <Grid item xs={3}>
        <span className="batch_text">Qty for stock type change</span>
        <span style={{ color: "red" }}>*</span>
      </Grid>
      <Grid item xs={9} display={"flex"} gap={"16px"}>
        <Grid item xs={otherUnitLabel ? 4 : 6}>
          <TextField
            fullWidth
            name="quantityCs"
            value={tempBatchData?.quantityCs || ""}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value)) {
                updateBatchWiseData(e.target.name, value);
              }
            }}
            onBlur={handleFocusOut}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <span>{caseUnitLabel}</span>
                </InputAdornment>
              ),
            }}
            sx={disableCs ? nonEditableGreyTheme : customMuiInputStyle}
            variant="outlined"
            size="small"
            error={quantityError}
            helperText={quantityError ? "Updated Qty can't be more than Current Qty" : ""}
            FormHelperTextProps={{
              sx: {
                fontSize: "11px",
                lineHeight: "15px",
              },
            }}
            onKeyDown={handleKeyDown}
            disabled={disableCs}
          />
        </Grid>
        {otherUnitLabel && (
          <Grid item xs={otherUnitLabel ? 4 : 6}>
            <TextField
              fullWidth
              name="quantityOther"
              value={tempBatchData?.quantityOther || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value)) {
                  updateBatchWiseData(e.target.name, value);
                }
              }}
              onBlur={handleFocusOut}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <span>{otherUnitName ? otherUnitName: otherUnitLabel}</span>
                  </InputAdornment>
                ),
              }}
              sx={disableOther ? nonEditableGreyTheme : customMuiInputStyle}
              variant="outlined"
              size="small"
              error={quantityError}
              onKeyDown={handleKeyDown}
              disabled={disableOther}
            />
          </Grid>
        )}
        <Grid item xs={otherUnitLabel ? 4 : 6}>
          <TextField
            fullWidth
            name="quantityPc"
            value={tempBatchData?.quantityPc || ""}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value)) {
                updateBatchWiseData(e.target.name, value);
              }
            }}
            onBlur={handleFocusOut}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <span>{packUnitLabel}</span>
                </InputAdornment>
              ),
            }}
            sx={disablePc ? nonEditableGreyTheme : customMuiInputStyle}
            variant="outlined"
            size="small"
            error={quantityError}
            onKeyDown={handleKeyDown}
            disabled={disablePc}
          />
        </Grid>
      </Grid>
      <Grid item xs={3}>
        <span className="batch_text">Updated Stock Type</span>
        <span style={{ color: "red" }}>*</span>
      </Grid>
      <Grid item xs={9}>
        <Autocomplete
          options={stockTypeTransferMapping[formData.stockType]}
          getOptionLabel={(option) => option}
          value={tempBatchData?.updatedType}
          onChange={(e, value) => updateBatchWiseData("updatedType", value ?? "")}
          disabled={
            !enableDropDowns || stockTypeTransferMapping[formData.stockType]?.length === 1
          }
          sx={{ width: "100%" }}
          renderInput={(params) => (
            <TextField
              {...params}
              name="updatedType"
              variant="outlined"
              size="small"
              sx={customMuiInputStyle}
              required
              error={Boolean(mandatoryFieldError) && !params.inputProps.value}
              helperText={
                mandatoryFieldError &&
                params.inputProps.value === "" &&
                "Please select Updated Type before proceeding"
              }
              placeholder="Select a type"
            />
          )}
        />
      </Grid>
      <Grid item xs={3}>
        <span className="batch_text">Reason</span>
        <span style={{ color: "red" }}>*</span>
      </Grid>
      <Grid item xs={9}>
        <Autocomplete
          value={tempBatchData?.reason}
          options={stockUpdateReasons}
          getOptionLabel={(option) => option}
          onChange={(e, value) => updateBatchWiseData("reason", value ?? "")}
          sx={{ width: "100%" }}
          disabled={!(enableDropDowns && tempBatchData?.updatedType)}
          renderInput={(params) => (
            <TextField
              {...params}
              name="reason"
              variant="outlined"
              size="small"
              sx={customMuiInputStyle}
              required
              error={Boolean(mandatoryFieldError) && !params.inputProps.value}
              helperText={
                mandatoryFieldError &&
                params.inputProps.value === "" &&
                "Please select reason before proceeding"
              }
              placeholder="Select a reason"
            />
          )}
        />
      </Grid>
      <div className="update_batch_buttons">
        <span onClick={clearBatch}>
          <CancelIcon style={{ color: "red", fontSize: "25px", cursor: "pointer" }} />
        </span>

        <span onClick={updateBatch}>
          <CheckCircleIcon style={{ color: "green", fontSize: "25px", cursor: "pointer" }} />
        </span>
      </div>
    </Grid>
  );
};

interface BatchComponentProps<T extends StockAdjustFormData | StockTypeUpdateFormData> {
  batchNumber: string;
  currentCs: string;
  currentPc: string;
  currentOther: string;
  showComponentV2?: boolean;
  formData: T;
  setFormData: React.Dispatch<React.SetStateAction<T>>;
  expandedBatchId: string | null;
  onExpand: (batchId: string) => void;
  mandatoryFieldError: boolean;
  setMandatoryFieldError: React.Dispatch<React.SetStateAction<boolean>>;
  uom: string;
  shouldPrefillQuantities?: boolean;
  otherUnitName?: string
}

const BatchComponent = <T extends StockAdjustFormData | StockTypeUpdateFormData>({
  batchNumber,
  currentCs,
  currentOther,
  currentPc,
  showComponentV2,
  formData,
  setFormData,
  onExpand,
  expandedBatchId,
  mandatoryFieldError,
  setMandatoryFieldError,
  uom,
  shouldPrefillQuantities = false,
  otherUnitName
}: BatchComponentProps<T>) => {
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
  const { caseUnitLabel, otherUnitLabel, packUnitLabel } = ConfigDictionary;
  const disableCs = !uom?.toLowerCase().includes("cs") && !uom?.toLowerCase().includes("all");
  const disablePc = !uom?.toLowerCase().includes("ea") && !uom?.toLowerCase().includes("all");
  const disableOther = !uom?.toLowerCase().includes("other") && !uom?.toLowerCase().includes("all");

  const initialBatchData = {
    quantityCs: "",
    quantityPc: "",
    quantityOther: "",
    reason: "",
    updatedType: "",
  };

  const [quantityError, setQuantityError] = useState(false);

  // Helper function to check if quantities have actually changed
  const hasQuantityChanged = (batchData: any) => {
    if (!batchData) return false;
    return (
      batchData.quantityCs !== currentCs ||
      batchData.quantityPc !== currentPc ||
      batchData.quantityOther !== currentOther
    );
  };

  const isUpdated =
    formData?.batchWiseData[batchNumber] &&
    hasQuantityChanged(formData?.batchWiseData[batchNumber]) &&
    ((!showComponentV2 && formData?.batchWiseData[batchNumber]?.reason) ||
      (formData?.batchWiseData[batchNumber]?.reason &&
        formData?.batchWiseData[batchNumber]?.updatedType));
  
  const isExpanded = expandedBatchId === batchNumber;
  const [tempBatchData, setTempBatchData] = useState<{ [key: string]: any }>(initialBatchData);
  
  // Check if temp batch data has actually changed from current quantities
  const hasTempQuantityChangedManual =
    (tempBatchData?.quantityCs !== "" && tempBatchData.quantityCs !== currentCs) ||
    (tempBatchData?.quantityPc !== "" && tempBatchData.quantityPc !== currentPc) ||
    (tempBatchData?.quantityOther !== "" && tempBatchData.quantityOther !== currentOther);

  // For stock type update, a change means any quantity greater than 0 is entered
  const hasTempQuantityChangedStockUpdate =
    (parseInt(tempBatchData?.quantityCs || "0") > 0) ||
    (parseInt(tempBatchData?.quantityPc || "0") > 0) ||
    (parseInt(tempBatchData?.quantityOther || "0") > 0);

  const hasTempQuantityChanged = showComponentV2
    ? hasTempQuantityChangedStockUpdate
    : hasTempQuantityChangedManual;

  const enableDropDowns = hasTempQuantityChanged;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (["e", "E", "+", "-"].includes(event.key)) {
      event.preventDefault(); // Disallow "e", "E", "+", and "-" keys
    }
  };

  const canOthersExpand = () => {
    const isExpandedBatchUpdated =
      expandedBatchId != null &&
      hasTempQuantityChanged &&
      ((!showComponentV2 && tempBatchData?.reason) ||
        (tempBatchData?.reason && tempBatchData?.updatedType));
    const canOthersExpand =
      expandedBatchId == null ||
      !hasTempQuantityChanged ||
      isExpandedBatchUpdated;
    return canOthersExpand;
  };

  const toggleExpand = () => {
    if (canOthersExpand()) {
      // Initialize with current quantities if batch data doesn't exist yet
      const existingBatchData = formData.batchWiseData[batchNumber];
      if (existingBatchData) {
        setTempBatchData(existingBatchData);
      } else {
        // Only prefill quantities if shouldPrefillQuantities is true (for Manual Stock Adjustment)
        // For Stock Type Update, start with empty quantities
        setTempBatchData({
          quantityCs: shouldPrefillQuantities ? currentCs : "",
          quantityPc: shouldPrefillQuantities ? currentPc : "",
          quantityOther: shouldPrefillQuantities ? currentOther : "",
          reason: "",
          updatedType: "",
        });
      }
      onExpand(batchNumber);
      setMandatoryFieldError(false);
    } else {
      setMandatoryFieldError(true);
      console.log("error in validation");
    }
  };

  const updateBatchWiseData = (fieldName: string, value: string) => {
    //reason changes based on stock type
    if (fieldName === "updatedType") {
      setTempBatchData((prev) => ({
        ...prev,
        reason: "",
        [fieldName]: value,
      }));
    } else {
      setTempBatchData((prev) => ({
        ...prev,
        [fieldName]: value,
      }));
    }
  };

  const updateBatch = async () => {
    if (showComponentV2) {
      const currentQtyObject = {
        skuCode: formData.skuCode,
        totalCaseQty: parseInt(currentCs, 10) || 0,
        totalPieceQty: parseInt(currentPc, 10) || 0,
        totalOtherQty: parseInt(currentOther, 10) || 0,
      };

      const updatedQtyObject = {
        skuCode: formData.skuCode,
        totalCaseQty: parseInt(tempBatchData.quantityCs, 10) || 0,
        totalPieceQty: parseInt(tempBatchData.quantityPc, 10) || 0,
        totalOtherQty: parseInt(tempBatchData.quantityOther, 10) || 0,
      };

      const currentNq = await getNqQuantity(currentQtyObject);
      const updatedNq = await getNqQuantity(updatedQtyObject);

      if (updatedNq > currentNq) {
        setQuantityError(true);
        return;
      }
    }

    if (
      hasTempQuantityChanged &&
      tempBatchData?.reason &&
      (!showComponentV2 || tempBatchData?.updatedType)
    ) {
      // Only save if quantities actually changed and reason is provided
      const updatedBatchWiseData = {
        ...formData.batchWiseData,
        [batchNumber]: tempBatchData,
      };
      setFormData({
        ...formData,
        batchWiseData: updatedBatchWiseData,
      });
      onExpand(batchNumber);
      setQuantityError(false);
    } else if (hasTempQuantityChanged) {
      // Quantities changed but no reason provided
      setQuantityError(false);
      setMandatoryFieldError(true);
    } else {
      // No changes made, just close without error
      onExpand(batchNumber);
      setQuantityError(false);
      setMandatoryFieldError(false);
    }
  };

  const clearBatch = () => {
    // Create a new object instead of mutating the existing one
    const updatedBatchWiseData = { ...formData.batchWiseData };
    delete updatedBatchWiseData[batchNumber];
    
    // Reset to default state - prefill with current quantities only if shouldPrefillQuantities is true
    setTempBatchData({
      quantityCs: shouldPrefillQuantities ? currentCs : "",
      quantityPc: shouldPrefillQuantities ? currentPc : "",
      quantityOther: shouldPrefillQuantities ? currentOther : "",
      reason: "",
      updatedType: "",
    });
    
    setFormData({
      ...formData,
      batchWiseData: updatedBatchWiseData,
    });
    setMandatoryFieldError(false);
    setQuantityError(false);
  };

  // Handle focus out to normalize quantities (following senior's pattern)
  const handleFocusOut = async () => {
    const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
    
    if (!ConfigDictionary.uomConversion) {
      return;
    }

    try {
      const qtyObject = {
        skuCode: formData.skuCode,
        totalCaseQty: tempBatchData?.quantityCs ? parseInt(tempBatchData.quantityCs) : 0,
        totalPieceQty: tempBatchData?.quantityPc ? parseInt(tempBatchData.quantityPc) : 0,
        totalOtherQty: tempBatchData?.quantityOther ? parseInt(tempBatchData.quantityOther) : 0,
      };

      await convertToMaxQty(qtyObject, formData.skuCode, uom);
      
      const normalizedBatchData = {
        ...tempBatchData,
        quantityCs: qtyObject.totalCaseQty + "",
        quantityPc: qtyObject.totalPieceQty + "",
        quantityOther: qtyObject.totalOtherQty + ""
      };
      
      setTempBatchData(normalizedBatchData);
    } catch (error) {
      console.error("Error in handleFocusOut:", error);
    }
  };

  useEffect(() => {
    setTempBatchData(initialBatchData);
    setMandatoryFieldError(false);
    setQuantityError(false);
  }, [formData.stockType]);

  // Auto-prefill updatedType when there's only one option available
  useEffect(() => {
    const availableOptions = stockTypeTransferMapping[formData.stockType];
    if (availableOptions && availableOptions.length === 1 && !tempBatchData?.updatedType) {
      updateBatchWiseData("updatedType", availableOptions[0]);
    }
  }, [formData.stockType, tempBatchData?.updatedType]);

  return (
    <Box
      sx={{
        border: "1px solid #ddd",
        borderRadius: "4px",
        padding: "0 12px",
        margin: "10px 0",
        ...(isUpdated ? selectedLightBlueTheme : {}),
      }}
    >
      <Grid container alignItems="center" onClick={toggleExpand} sx={{ cursor: "pointer" }}>
        <Grid item xs>
          <span className="batch_text">
            {batchNumber.includes("unassigned") ? "Unassigned Qty." : batchNumber}
          </span>
        </Grid>
        {isUpdated && (
          <Grid
            item
            display="flex"
            alignItems={"center"}
            gap={"2px"}
            color={"#227C9D"}
            fontSize={"12px"}
          >
            <CheckCircleIcon sx={{ fontSize: "12px !important" }} />
            Updated
          </Grid>
        )}
        <Grid item>
          <IconButton size="small">{isExpanded ? <ArrowDropUp /> : <ArrowDropDown />}</IconButton>
        </Grid>
      </Grid>
      <Collapse in={isExpanded}>
        <Box mt={2}>
          {!showComponentV2 ? (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={3}>
                <span className="batch_text">Current Quantity</span>
              </Grid>
              <Grid item xs={otherUnitLabel ? 3 : 4.5}>
                <TextField
                  fullWidth
                  name="quantityCs"
                  type="number"
                  value={currentCs}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {" "}
                        <span>{caseUnitLabel}</span>
                      </InputAdornment>
                    ),
                  }}
                  disabled={true}
                  sx={nonEditableGreyTheme}
                  inputProps={{ readOnly: true }}
                />
              </Grid>
              {otherUnitLabel && (
                <Grid item xs={otherUnitLabel ? 3 : 4.5}>
                  <TextField
                    fullWidth
                    name="quantityOther"
                    type="number"
                    value={currentOther}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <span>{otherUnitName? otherUnitName:otherUnitLabel}</span>
                        </InputAdornment>
                      ),
                    }}
                    disabled={true}
                    sx={nonEditableGreyTheme}
                    inputProps={{ readOnly: true }}
                  />
                </Grid>
              )}
              <Grid item xs={otherUnitLabel ? 3 : 4.5}>
                <TextField
                  fullWidth
                  name="quantityPc"
                  type="number"
                  value={currentPc}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <span>{packUnitLabel}</span>
                      </InputAdornment>
                    ),
                  }}
                  disabled={true}
                  sx={nonEditableGreyTheme}
                  inputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={3}>
                <span className="batch_text">Updated Quantity</span>
                <span style={{ color: "red" }}>*</span>
              </Grid>
              <Grid item xs={otherUnitLabel ? 3 : 4.5}>
                <TextField
                  name="quantityCs"
                  value={tempBatchData?.quantityCs ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) {
                      updateBatchWiseData(e.target.name, value);
                    }
                  }}
                  onBlur={handleFocusOut}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <span>{caseUnitLabel}</span>
                      </InputAdornment>
                    ),
                  }}
                  sx={disableCs ? nonEditableGreyTheme : customMuiInputStyle}
                  onKeyDown={handleKeyDown}
                  disabled={disableCs}
                />
              </Grid>
              {otherUnitLabel && (
                <Grid item xs={otherUnitLabel ? 3 : 4.5}>
                  <TextField
                    name="quantityOther"
                    value={tempBatchData?.quantityOther ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*$/.test(value)) {
                        updateBatchWiseData(e.target.name, value);
                      }
                    }}
                    onBlur={handleFocusOut}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <span>{otherUnitName ? otherUnitName: otherUnitLabel}</span>
                        </InputAdornment>
                      ),
                    }}
                    sx={disableOther ? nonEditableGreyTheme : customMuiInputStyle}
                    onKeyDown={handleKeyDown}
                    disabled={disableOther}
                  />
                </Grid>
              )}
              <Grid item xs={otherUnitLabel ? 3 : 4.5}>
                <TextField
                  name="quantityPc"
                  value={tempBatchData?.quantityPc ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) {
                      updateBatchWiseData(e.target.name, value);
                    }
                  }}
                  onBlur={handleFocusOut}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <span>{packUnitLabel}</span>
                      </InputAdornment>
                    ),
                  }}
                  sx={disablePc ? nonEditableGreyTheme : customMuiInputStyle}
                  onKeyDown={handleKeyDown}
                  disabled={disablePc}
                />
              </Grid>
              <Grid item xs={3}>
                <span className="batch_text">Reason</span>
                <span style={{ color: "red" }}>*</span>
              </Grid>
              <Grid item xs={9}>
                <Autocomplete
                  value={tempBatchData?.reason ?? ""}
                  options={reasons}
                  getOptionLabel={(option) => option}
                  onChange={(e, value) => updateBatchWiseData("reason", value ?? "")}
                  sx={{ width: "100%" }}
                  disabled={!enableDropDowns}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="reason"
                      variant="outlined"
                      size="small"
                      sx={customMuiInputStyle}
                      required
                      error={Boolean(mandatoryFieldError) && !params.inputProps.value}
                      helperText={
                        mandatoryFieldError &&
                        params.inputProps.value === "" &&
                        "Please select reason before proceeding"
                      }
                      placeholder="Select a reason"
                    />
                  )}
                />
              </Grid>
              <div className="update_batch_buttons">
                <span onClick={clearBatch}>
                  <CancelIcon style={{ color: "red", fontSize: "25px", cursor: "pointer" }} />
                </span>
                <span onClick={updateBatch}>
                  <CheckCircleIcon
                    style={{ color: "green", fontSize: "25px", cursor: "pointer" }}
                  />
                </span>
              </div>
            </Grid>
          ) : (
            <BatchComponentV2
              formData={formData}
              updateBatchWiseData={updateBatchWiseData}
              currentCs={currentCs}
              currentPc={currentPc}
              currentOther={currentOther}
              mandatoryFieldError={mandatoryFieldError}
              updateBatch={updateBatch}
              clearBatch={clearBatch}
              tempBatchData={tempBatchData}
              quantityError={quantityError}
              uom={uom}
              otherUnitName={otherUnitName}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default BatchComponent;
